import { View, Text, Pressable, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import Unicon from '@/components/General/Unicon';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Post from '@/components/Connect/Boards/Post';
import Toggle from '@/components/General/inputs/Toggle';
import { Tooltip } from '@/components/General/Tooltip';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import useBoardsFeedStore from '@/data/store/useBoardsFeedStore';
import FeatureIcon from '@/components/General/FeatureIcon';
import CutCard from '@/components/General/CutCard';
import dashboardStyles from './dashboardStyles';

const MAX_POSTS = 10;

const MyBoardsFeedSection = () => {
    const router = useRouter();
    const queryClient = useQueryClient();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: boardsCurrentUser, isFetching: fetchingBoards } = Query.boards.all();
    const showAllBoards = useBoardsFeedStore(state => state.showAllBoards);
    const toggleShowAllBoards = useBoardsFeedStore(state => state.toggleShowAllBoards);

    const canViewAllBoards = !!loggedInUser?.isAdmin || !!loggedInUser?.can?.('View All Boards');

    const messageBoards = useMemo(
        () => (boardsCurrentUser || []).filter(b => ['MESSAGE_BOARD', 'ANNOUNCEMENTS'].includes(b.type)),
        [boardsCurrentUser]
    );

    const userRoleIds = useMemo(() => new Set(
        (loggedInUser?.Roles || loggedInUser?.roles || []).map(r => r.id || r.roleID).filter(Boolean)
    ), [loggedInUser]);
    const isBoardMember = useMemo(() => (board) => {
        if (board.createdByID && board.createdByID === loggedInUser?.id) return true;
        if (!board.Roles || board.Roles.length === 0) return true;
        return board.Roles.some(r => userRoleIds.has(r.roleID));
    }, [loggedInUser, userRoleIds]);

    const boardIDs = useMemo(() => {
        //Hidden (per-user) and archived (org-wide, read-dead) boards never
        //contribute posts to My Feed. Mirrors feed.jsx.
        const feedBoards = messageBoards.filter(b => (b.type === 'ANNOUNCEMENTS' || !b.hiddenForUser) && !b.isArchived);
        if (canViewAllBoards && showAllBoards) return feedBoards.map(b => b.id);
        return feedBoards.filter(isBoardMember).map(b => b.id);
    }, [messageBoards, canViewAllBoards, showAllBoards, isBoardMember]);

    const { data: posts, isLoading: loadingPosts } = Query.posts.myBoardsFeed({ boardIDs });
    const boardIDSet = useMemo(() => new Set(boardIDs), [boardIDs]);
    const recentPosts = useMemo(() => posts?.pages?.[0]?.data?.filter(p => boardIDSet.has(p?.boardID))?.slice(0, MAX_POSTS) || [], [posts, boardIDSet]);

    // Refetch when the showAllBoards toggle changes — same query key, new boardIDs closure.
    // Skip first run to avoid a redundant invalidate on mount.
    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        queryClient.invalidateQueries({ queryKey: QueryKeys.posts.myBoardsFeed() });
    }, [showAllBoards]);

    const toggleRow = canViewAllBoards ? (
        <View style={styles.toggleRow}>
            <Pressable
                style={styles.toggleLabelWrap}
                onPress={() => toggleShowAllBoards()}
                testID="dashboard-boards-feed-show-all-toggle-row"
            >
                <Text style={styles.toggleLabel}>Show All Boards</Text>
                <Tooltip
                    title="Show All Boards"
                    subtitle="Shows posts from boards you're not a member of, since you have permission to view all boards."
                    appearOnTouch
                >
                    <Unicon name="help-circle-outline" size={14} colorVariant="textTertiary" />
                </Tooltip>
            </Pressable>
            <Toggle
                value={showAllBoards}
                onValueChange={() => toggleShowAllBoards()}
                testID="dashboard-boards-feed-show-all-toggle"
            />
        </View>
    ) : null;

    if (fetchingBoards || loadingPosts) {
        return (
            <CutCard testID="dashboard-section-myBoardsFeed" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
                <View style={[styles.header, dashboardStyles.header]}>
                    <View style={[styles.titleRow, dashboardStyles.titleRow]}>
                        <FeatureIcon feature="connect" />
                        <Text style={[styles.title, dashboardStyles.title]}>My Boards Feed</Text>
                    </View>
                </View>
                {toggleRow}
                <View style={styles.loadingWrap}>
                    <ContentLoader containerHeight={200} />
                </View>
            </CutCard>
        );
    }

    if (recentPosts.length === 0) {
        return (
            <CutCard testID="dashboard-section-myBoardsFeed" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
                <View style={[styles.header, dashboardStyles.header]}>
                    <View style={[styles.titleRow, dashboardStyles.titleRow]}>
                        <FeatureIcon feature="connect" />
                        <Text style={[styles.title, dashboardStyles.title]}>My Boards Feed</Text>
                    </View>
                    <Pressable hitSlop={20} onPress={() => router.push('/connect/boards/feed')} style={styles.linkBtn}>
                        <Text style={styles.linkText}>View All</Text>
                        <Unicon name="chevron-forward-outline" size={12} colorVariant="primary" />
                    </Pressable>
                </View>
                {toggleRow}
                <View style={styles.emptyWrap}>
                    <EmptyListPlaceholder size={90} type="complete" message="No posts from your boards yet!" spacing={8} />
                </View>
            </CutCard>
        );
    }

    return (
        <CutCard testID="dashboard-section-myBoardsFeed" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
            <View style={[styles.header, dashboardStyles.header]}>
                <View style={[styles.titleRow, dashboardStyles.titleRow]}>
                    <FeatureIcon feature="connect" />
                    <Text style={[styles.title, dashboardStyles.title]}>My Boards Feed</Text>
                </View>
                <Pressable hitSlop={20} onPress={() => router.push('/connect/boards/feed')} style={styles.linkBtn}>
                    <Text style={styles.linkText}>View All</Text>
                    <Unicon name="chevron-forward-outline" size={12} colorVariant="primary" />
                </Pressable>
            </View>
            {toggleRow}

            <ScrollView
                testID="dashboard-feed-scroll"
                style={[styles.postsScroll, dashboardStyles.scroll]}
                contentContainerStyle={styles.postsContent}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
            >
                {recentPosts.map((item) => <Post key={item.id} post={item} showBoardName variant="alternate" />)}
                <Pressable hitSlop={20} onPress={() => router.push('/connect/boards/feed')} style={styles.viewAllFooter}>
                    <Text style={styles.viewAllFooterText}>View My Feed</Text>
                    <Unicon name="chevron-forward-outline" size={14} colorVariant="primary" />
                </Pressable>
            </ScrollView>
        </CutCard>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
    card: {
        width: {
            xs: "100%",
            lg: "48.5%",
        },
        borderRadius: roundness.md,
        paddingHorizontal: 1,
        backgroundColor: colors.surface,
        overflow: "hidden",
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: spacing.md,
        paddingTop: spacing.md,
        paddingBottom: spacing.sm,
    },
    toggleRow: {
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs + 2,
        marginHorizontal: spacing.md,
        marginBottom: spacing.sm,
        borderRadius: roundness.sm,
    },
    toggleLabelWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    toggleLabel: {
        color: colors.textSecondary,
        fontSize: typography.caption,
        fontFamily: fonts.body,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs + 2,
    },
    iconWrap: {
        width: 24,
        height: 24,
        borderRadius: 6,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    linkBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        paddingVertical: spacing.xs,
        paddingHorizontal: spacing.xs + 2,
        borderRadius: 6,
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    linkText: {
        color: colors.primary,
        fontSize: typography.caption,
        fontFamily: fonts.heading,
    },
    loadingWrap: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: spacing.md,
        paddingBottom: spacing.md,
        backgroundColor: colors.background,
    },
    emptyWrap: {
        paddingHorizontal: spacing.md,
        paddingBottom: spacing.md,
        backgroundColor: colors.background,
    },
    postsScroll: {
        backgroundColor: colors.background,
    },
    postsContent: {
        paddingVertical: spacing.xs,
    },
    footerLoader: {
        paddingVertical: spacing.sm,
    },
    viewAllFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        paddingVertical: spacing.sm + 2,
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    viewAllFooterText: {
        color: colors.primary,
        fontSize: typography.caption,
        fontFamily: fonts.heading,
    },
}));

export default MyBoardsFeedSection;
